import React, {useContext, useEffect, useState} from "react";
import {Modal, Nav, NavItem} from "react-bootstrap";
import Switch from "react-bootstrap-switch";
import {ApiContext} from '../../../api/Api';

const emptyService = {
    name: "",
    durationMinutes: 20,
    requiresStaff: true,
    resourceType: "",
    observations: "",
    active: true
};

const normalizeCollection = value => {
    if (Array.isArray(value))
        return value;

    if (value && Array.isArray(value["hydra:member"]))
        return value["hydra:member"];

    return [];
};

const SPA_LOUNGE_NAMES = [
    "L 19",
    "L19",
    "TGLE",
    "TERRAZA",
    "CELESTE",
    "HAVEN",
    "AIFA",
    "HSL",
    "HACIENDA SANTA LUCIA",
    "HACIENDA SANTA LUCÍA"
];

const isSpaOperationalLounge = lounge => {
    const name = String(lounge && lounge.name || "")
        .trim()
        .toUpperCase();

    return SPA_LOUNGE_NAMES.includes(name);
};

const relationId = value => {
    if (!value)
        return "";

    if (typeof value === "object")
        return value.id || "";

    const parts = value.split("/");
    return parts[parts.length - 1];
};

const spaUi = {
    panel: {
        backgroundColor: "#fff",
        border: "1px solid #e3e8ee",
        borderRadius: 4,
        marginBottom: 20,
        boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
        overflow: "hidden"
    },

    panelHeader: {
        padding: "12px 14px",
        backgroundColor: "#f7fafd",
        borderBottom: "1px solid #e3e8ee"
    },

    panelTitle: {
        margin: 0,
        fontSize: 16,
        fontWeight: 600,
        color: "#34495e"
    },

    panelDescription: {
        marginTop: 2,
        marginBottom: 0,
        fontSize: 12,
        color: "#7f8c8d"
    },

    table: {
        marginBottom: 0
    },

    tableHead: {
        backgroundColor: "#fafbfc",
        color: "#6b7785",
        fontSize: 11,
        textTransform: "uppercase"
    },

    badgeActive: {
        display: "inline-block",
        minWidth: 58,
        padding: "3px 9px",
        borderRadius: 12,
        backgroundColor: "#dff3e7",
        color: "#21834a",
        fontSize: 12,
        textAlign: "center"
    },

    badgeInactive: {
        display: "inline-block",
        minWidth: 58,
        padding: "3px 9px",
        borderRadius: 12,
        backgroundColor: "#edf0f3",
        color: "#65727e",
        fontSize: 12,
        textAlign: "center"
    },

    badgeYes: {
        display: "inline-block",
        minWidth: 34,
        padding: "3px 9px",
        borderRadius: 12,
        backgroundColor: "#dff3e7",
        color: "#21834a",
        fontSize: 12,
        textAlign: "center"
    },

    badgeNo: {
        display: "inline-block",
        minWidth: 34,
        padding: "3px 9px",
        borderRadius: 12,
        backgroundColor: "#fde4e4",
        color: "#b33a3a",
        fontSize: 12,
        textAlign: "center"
    }
};

const SpaConfiguration = () => {
    const api = useContext(ApiContext);

    const [services, setServices] = useState([]);
    const [resourceTypes, setResourceTypes] = useState([]);
    const [selectedService, setSelectedService] = useState(null);
    const [form, setForm] = useState({...emptyService});
    const [showForm, setShowForm] = useState(false);
    const [selectedSection, setSelectedSection] = useState("services");

    const [resources, setResources] = useState([]);
    const [lounges, setLounges] = useState([]);
    const [selectedResource, setSelectedResource] = useState(null);
    const [showResourceForm, setShowResourceForm] = useState(false);

    const emptyResource = {
        name: "",
        resourceType: "",
        lounge: "",
        status: "AVAILABLE",
        active: true
    };

    const [resourceForm, setResourceForm] = useState({...emptyResource});

    const emptyShift = {
        name: "",
        startTime: "",
        endTime: "",
        mealDurationMinutes: 30,
        isNight: false,
        active: true
    };

    const [shifts, setShifts] = useState([]);
    const [selectedShift, setSelectedShift] = useState(null);
    const [shiftForm, setShiftForm] = useState({...emptyShift});
    const [showShiftForm, setShowShiftForm] = useState(false);

    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");

    const loadData = async () => {
        setLoading(true);
        setError("");

        try {
            const servicesResult = await api.spaServices.get({
                filters: {pagination: false},
                customProp: "spaConfigurationServices"
            });

            const resourceTypesResult = await api.spaResourceTypes.get({
                filters: {pagination: false},
                customProp: "spaConfigurationResourceTypes"
            });

            const resourcesResult = await api.spaResources.get({
                filters: {
                    pagination: false,
                    "order[name]": "ASC"
                },
                customProp: "spaConfigurationResources"
            });

            const loungesResult = await api.lounges.get({
                filters: {
                    isLounge: true,
                    isActive: true,
                    pagination: false,
                    "order[name]": "ASC"
                },
                customProp: "spaConfigurationLounges"
            });

            const shiftsResult = await api.spaShifts.get({
                filters: {
                    pagination: false,
                    "order[name]": "ASC"
                },
                customProp: "spaConfigurationShifts"
            });

            setServices(normalizeCollection(servicesResult));
            setResourceTypes(normalizeCollection(resourceTypesResult));
            setResources(normalizeCollection(resourcesResult));
            setLounges(
                normalizeCollection(loungesResult)
                    .filter(isSpaOperationalLounge)
            );
            setShifts(normalizeCollection(shiftsResult));
        } catch (e) {
            setError("No fue posible cargar la configuración SPA.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    const startNew = () => {
        setSelectedService(null);
        setForm({...emptyService});
        setMessage("");
        setError("");
        setShowForm(true);
    };

    const closeForm = () => {
        setSelectedService(null);
        setForm({...emptyService});
        setShowForm(false);
        setError("");
    };

    const startEdit = service => {
        setSelectedService(service);

        setForm({
            name: service.name || "",
            durationMinutes: service.durationMinutes || 20,
            requiresStaff: service.requiresStaff !== false,
            resourceType: relationId(service.resourceType),
            observations: service.observations || "",
            active: service.active !== false
        });

        setMessage("");
        setError("");
        setShowForm(true);
    };

    const change = event => {
        const {name, value, type, checked} = event.target;

        setForm(current => ({
            ...current,
            [name]: type === "checkbox" ? checked : value
        }));
    };

    const save = async event => {
        event.preventDefault();

        setMessage("");
        setError("");

        const durationMinutes = Number(form.durationMinutes);

        if (!form.name.trim()) {
            setError("El nombre del servicio es obligatorio.");
            return;
        }

        if (!Number.isInteger(durationMinutes) || durationMinutes <= 0) {
            setError("La duración debe ser un número entero mayor a cero.");
            return;
        }

        const payload = {
            name: form.name.trim(),
            durationMinutes,
            requiresStaff: Boolean(form.requiresStaff),
            resourceType: form.resourceType
                ? `/api/spa_resource_types/${form.resourceType}`
                : null,
            observations: form.observations.trim() || null,
            active: Boolean(form.active)
        };

        setLoading(true);

        try {
            if (selectedService) {
                await api.spaServices.edit({
                    id: selectedService.id,
                    data: payload,
                    customProp: "spaConfigurationServiceSave"
                });

                setMessage("Servicio actualizado correctamente.");
            } else {
                await api.spaServices.create({
                    data: payload,
                    customProp: "spaConfigurationServiceSave"
                });

                setMessage("Servicio creado correctamente.");
            }

            setSelectedService(null);
            setForm({...emptyService});
            setShowForm(false);

            await loadData();
        } catch (e) {
            setError("No fue posible guardar el servicio SPA.");
        } finally {
            setLoading(false);
        }
    };

    const startNewResource = () => {
        setSelectedResource(null);
        setResourceForm({...emptyResource});
        setMessage("");
        setError("");
        setShowResourceForm(true);
    };

    const closeResourceForm = () => {
        setSelectedResource(null);
        setResourceForm({...emptyResource});
        setShowResourceForm(false);
        setError("");
    };

    const startEditResource = resource => {
        setSelectedResource(resource);
        setResourceForm({
            name: resource.name || "",
            resourceType: relationId(resource.resourceType),
            lounge: relationId(resource.lounge),
            status: resource.status || "AVAILABLE",
            active: resource.active !== false
        });
        setMessage("");
        setError("");
        setShowResourceForm(true);
    };

    const saveResource = async event => {
        event.preventDefault();
        setMessage("");
        setError("");

        if (!resourceForm.name.trim()) {
            setError("El nombre del recurso es obligatorio.");
            return;
        }

        if (!resourceForm.resourceType) {
            setError("Selecciona el tipo de recurso.");
            return;
        }

        if (!resourceForm.lounge) {
            setError("Selecciona la sala.");
            return;
        }

        const payload = {
            name: resourceForm.name.trim(),
            resourceType: `/api/spa_resource_types/${resourceForm.resourceType}`,
            lounge: `/api/lounges/${resourceForm.lounge}`,
            status: resourceForm.status,
            active: Boolean(resourceForm.active)
        };

        setLoading(true);

        try {
            if (selectedResource) {
                await api.spaResources.edit({
                    id: selectedResource.id,
                    data: payload,
                    customProp: "spaConfigurationResourceSave"
                });

                setMessage("Recurso actualizado correctamente.");
            } else {
                await api.spaResources.create({
                    data: payload,
                    customProp: "spaConfigurationResourceSave"
                });

                setMessage("Recurso creado correctamente.");
            }

            setSelectedResource(null);
            setResourceForm({...emptyResource});
            setShowResourceForm(false);

            await loadData();
        } catch (e) {
            setError("No fue posible guardar el recurso SPA.");
        } finally {
            setLoading(false);
        }
    };

    const loungeName = resource => {
        const id = relationId(resource.lounge);
        const lounge = lounges.find(item => item.id === id);

        return lounge ? lounge.name : "Sin sala";
    };

    const resourceStatusName = status =>
        status === "OUT_OF_SERVICE"
            ? "Fuera de servicio"
            : "Disponible";

    const startNewShift = () => {
        setSelectedShift(null);
        setShiftForm({...emptyShift});
        setMessage("");
        setError("");
        setShowShiftForm(true);
    };

    const closeShiftForm = () => {
        setSelectedShift(null);
        setShiftForm({...emptyShift});
        setShowShiftForm(false);
        setError("");
    };

    const startEditShift = shift => {
        setSelectedShift(shift);
        setShiftForm({
            name: shift.name || "",
            startTime: shift.startTime
                ? String(shift.startTime).substring(0, 5)
                : "",
            endTime: shift.endTime
                ? String(shift.endTime).substring(0, 5)
                : "",
            mealDurationMinutes:
                shift.mealDurationMinutes === null ||
                shift.mealDurationMinutes === undefined
                    ? 30
                    : shift.mealDurationMinutes,
            isNight: shift.isNight === true,
            active: shift.active !== false
        });
        setMessage("");
        setError("");
        setShowShiftForm(true);
    };

    const saveShift = async () => {
        const name = String(shiftForm.name || "").trim();
        const startTime = String(shiftForm.startTime || "").trim();
        const endTime = String(shiftForm.endTime || "").trim();
        const mealDurationMinutes = Number(
            shiftForm.mealDurationMinutes
        );

        setMessage("");
        setError("");

        if (!name) {
            setError("Captura el nombre del turno.");
            return;
        }

        if (!startTime || !endTime) {
            setError("Captura la hora inicial y la hora final.");
            return;
        }

        if (![30, 45, 60].includes(mealDurationMinutes)) {
            setError("La duración de comida debe ser 30, 45 o 60 minutos.");
            return;
        }

        const payload = {
            name,
            startTime,
            endTime,
            mealDurationMinutes,
            isNight: shiftForm.isNight === true,
            active: shiftForm.active !== false
        };

        setLoading(true);

        try {
            if (selectedShift && selectedShift.id) {
                await api.spaShifts.edit({
                    id: selectedShift.id,
                    data: payload,
                    customProp: "spaConfigurationShiftUpdate"
                });
            } else {
                await api.spaShifts.create({
                    data: payload,
                    customProp: "spaConfigurationShiftCreate"
                });
            }

            setShowShiftForm(false);
            setSelectedShift(null);
            setShiftForm({...emptyShift});

            await loadData();

            setMessage(
                selectedShift
                    ? "Turno actualizado correctamente."
                    : "Turno creado correctamente."
            );
        } catch (e) {
            setError(
                selectedShift
                    ? "No fue posible actualizar el turno."
                    : "No fue posible crear el turno."
            );
        } finally {
            setLoading(false);
        }
    };

    const resourceTypeName = service => {
        const id = relationId(service.resourceType);

        if (!id)
            return "Sin tipo";

        const type = resourceTypes.find(item => item.id === id);

        if (type)
            return type.name;

        if (
            service.resourceType &&
            typeof service.resourceType === "object" &&
            service.resourceType.name
        )
            return service.resourceType.name;

        return "Sin tipo";
    };

    return (
        <div className="container-fluid">
            <div className="mb-3">
                <h3 className="mb-1">Configuración SPA</h3>
                <p className="text-muted">
                    Administra los servicios y la operación del SPA.
                </p>
            </div>

            <div className="nav-container">
                <Nav
                    bsStyle="tabs"
                    bsClass="nav nav-icons"
                    activeKey={selectedSection}
                    onSelect={setSelectedSection}
                >
                    <NavItem eventKey="services">
                        <i className="fa fa-cutlery"/><br/> Servicios
                    </NavItem>

                    <NavItem eventKey="resources">
                        <i className="fa fa-cubes"/><br/> Recursos
                    </NavItem>

                    <NavItem eventKey="staff" disabled>
                        <i className="fa fa-users"/><br/> Personal
                    </NavItem>

                    <NavItem eventKey="shifts">
                        <i className="fa fa-clock-o"/><br/> Turnos
                    </NavItem>

                    <NavItem eventKey="schedules" disabled>
                        <i className="fa fa-calendar"/><br/> Horarios
                    </NavItem>

                    <NavItem eventKey="blocks" disabled>
                        <i className="fa fa-ban"/><br/> Bloqueos
                    </NavItem>
                </Nav>
            </div>

            {message &&
                <div className="alert alert-success">
                    {message}
                </div>
            }

            {error &&
                <div className="alert alert-danger">
                    {error}
                </div>
            }

            {selectedSection === "services" &&
            <div style={spaUi.panel}>

                <div style={spaUi.panelHeader}>
                    <div className="row">
                        <div className="col-sm-8">
                            <h4 style={spaUi.panelTitle}>
                                <i
                                    className="fa fa-list"
                                    style={{
                                        marginRight: 10,
                                        color: "#337ab7"
                                    }}
                                />
                                Servicios
                            </h4>

                            <p style={spaUi.panelDescription}>
                                Configura duración, personal requerido y tipo de recurso.
                            </p>
                        </div>

                        <div
                            className="col-sm-4 text-right"
                            style={{paddingTop: 2}}
                        >
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={startNew}
                                disabled={loading}
                            >
                                <i
                                    className="fa fa-plus"
                                    style={{marginRight: 7}}
                                />
                                Nuevo servicio
                            </button>
                        </div>
                    </div>
                </div>

                <div className="table-responsive">
                    <table
                        className="table table-hover"
                        style={spaUi.table}
                    >
                        <thead style={spaUi.tableHead}>
                            <tr>
                                <th>Servicio</th>
                                <th>Duración</th>
                                <th>Requiere personal</th>
                                <th>Tipo de recurso</th>
                                <th>Estado</th>
                                <th className="text-right">Acciones</th>
                            </tr>
                        </thead>

                        <tbody>
                            {services.map(service => (
                                <tr key={service.id}>
                                    <td>
                                        <strong>{service.name}</strong>
                                    </td>

                                    <td>
                                        {service.durationMinutes} min
                                    </td>

                                    <td>
                                        <span
                                            style={
                                                service.requiresStaff
                                                    ? spaUi.badgeYes
                                                    : spaUi.badgeNo
                                            }
                                        >
                                            {service.requiresStaff ? "Sí" : "No"}
                                        </span>
                                    </td>

                                    <td>
                                        {resourceTypeName(service)}
                                    </td>

                                    <td>
                                        <span
                                            style={
                                                service.active
                                                    ? spaUi.badgeActive
                                                    : spaUi.badgeInactive
                                            }
                                        >
                                            {service.active
                                                ? "Activo"
                                                : "Inactivo"}
                                        </span>
                                    </td>

                                    <td className="text-right">
                                        <button
                                            type="button"
                                            className="btn btn-default btn-sm"
                                            onClick={() => startEdit(service)}
                                            disabled={loading}
                                        >
                                            <i
                                                className="fa fa-pencil"
                                                style={{marginRight: 6}}
                                            />
                                            Editar
                                        </button>
                                    </td>
                                </tr>
                            ))}

                            {!loading && services.length === 0 &&
                                <tr>
                                    <td
                                        colSpan="6"
                                        className="text-center text-muted"
                                        style={{padding: 25}}
                                    >
                                        No hay servicios configurados.
                                    </td>
                                </tr>
                            }
                        </tbody>
                    </table>
                </div>
            </div>
            }

            {selectedSection === "resources" &&
            <div style={spaUi.panel}>

                <div style={spaUi.panelHeader}>
                    <div className="row">
                        <div className="col-sm-8">
                            <h4 style={spaUi.panelTitle}>
                                <i
                                    className="fa fa-cubes"
                                    style={{
                                        marginRight: 10,
                                        color: "#337ab7"
                                    }}
                                />
                                Recursos
                            </h4>

                            <p style={spaUi.panelDescription}>
                                Administra los recursos físicos disponibles para la operación del SPA.
                            </p>
                        </div>

                        <div
                            className="col-sm-4 text-right"
                            style={{paddingTop: 2}}
                        >
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={startNewResource}
                                disabled={loading}
                            >
                                <i
                                    className="fa fa-plus"
                                    style={{marginRight: 7}}
                                />
                                Nuevo recurso
                            </button>
                        </div>
                    </div>
                </div>

                <div className="table-responsive">
                    <table
                        className="table table-hover"
                        style={spaUi.table}
                    >
                        <thead style={spaUi.tableHead}>
                            <tr>
                                <th>Recurso</th>
                                <th>Tipo de recurso</th>
                                <th>Sala</th>
                                <th>Disponibilidad</th>
                                <th>Estado</th>
                                <th className="text-right">Acciones</th>
                            </tr>
                        </thead>

                        <tbody>
                            {resources.map(resource =>
                                <tr key={resource.id}>
                                    <td>
                                        <strong>{resource.name}</strong>
                                    </td>

                                    <td>
                                        {resourceTypeName(resource)}
                                    </td>

                                    <td>
                                        {loungeName(resource)}
                                    </td>

                                    <td>
                                        <span
                                            style={
                                                resource.status === "AVAILABLE"
                                                    ? spaUi.badgeActive
                                                    : spaUi.badgeInactive
                                            }
                                        >
                                            {resourceStatusName(resource.status)}
                                        </span>
                                    </td>

                                    <td>
                                        <span
                                            style={
                                                resource.active !== false
                                                    ? spaUi.badgeActive
                                                    : spaUi.badgeInactive
                                            }
                                        >
                                            {resource.active !== false
                                                ? "Activo"
                                                : "Inactivo"}
                                        </span>
                                    </td>

                                    <td className="text-right">
                                        <button
                                            type="button"
                                            className="btn btn-default btn-sm"
                                            onClick={() =>
                                                startEditResource(resource)
                                            }
                                            disabled={loading}
                                        >
                                            <i
                                                className="fa fa-pencil"
                                                style={{marginRight: 6}}
                                            />
                                            Editar
                                        </button>
                                    </td>
                                </tr>
                            )}

                            {!loading && resources.length === 0 &&
                                <tr>
                                    <td
                                        colSpan="6"
                                        className="text-center text-muted"
                                        style={{padding: 25}}
                                    >
                                        No hay recursos SPA configurados.
                                    </td>
                                </tr>
                            }
                        </tbody>
                    </table>
                </div>
            </div>
            }

            {selectedSection === "shifts" &&
            <div style={spaUi.panel}>
                <div style={spaUi.panelHeader}>
                    <div className="row">
                        <div className="col-sm-8">
                            <h4 style={spaUi.panelTitle}>
                                <i
                                    className="fa fa-clock-o"
                                    style={{
                                        marginRight: 10,
                                        color: "#337ab7"
                                    }}
                                />
                                Turnos
                            </h4>

                            <p style={spaUi.panelDescription}>
                                Administra los turnos disponibles para el personal del SPA.
                            </p>
                        </div>

                        <div
                            className="col-sm-4 text-right"
                            style={{paddingTop: 2}}
                        >
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={startNewShift}
                                disabled={loading}
                            >
                                <i
                                    className="fa fa-plus"
                                    style={{marginRight: 7}}
                                />
                                Nuevo turno
                            </button>
                        </div>
                    </div>
                </div>

                <div className="table-responsive">
                    <table
                        className="table table-hover"
                        style={spaUi.table}
                    >
                        <thead style={spaUi.tableHead}>
                            <tr>
                                <th>Turno</th>
                                <th>Comida</th>
                                <th>Estado</th>
                                <th className="text-right">Acciones</th>
                            </tr>
                        </thead>

                        <tbody>
                            {shifts.map(shift =>
                                <tr key={shift.id}>
                                    <td>
                                        <strong>{shift.name}</strong>
                                    </td>

                                    <td>
                                        {shift.mealDurationMinutes || 0} min
                                    </td>

                                    <td>
                                        <span
                                            style={
                                                shift.active !== false
                                                    ? spaUi.badgeActive
                                                    : spaUi.badgeInactive
                                            }
                                        >
                                            {shift.active !== false
                                                ? "Activo"
                                                : "Inactivo"}
                                        </span>
                                    </td>

                                    <td className="text-right">
                                        <button
                                            type="button"
                                            className="btn btn-default btn-sm"
                                            onClick={() =>
                                                startEditShift(shift)
                                            }
                                            disabled={loading}
                                        >
                                            <i
                                                className="fa fa-pencil"
                                                style={{marginRight: 6}}
                                            />
                                            Editar
                                        </button>
                                    </td>
                                </tr>
                            )}

                            {!loading && shifts.length === 0 &&
                                <tr>
                                    <td
                                        colSpan="4"
                                        className="text-center text-muted"
                                        style={{padding: 25}}
                                    >
                                        No hay turnos SPA configurados.
                                    </td>
                                </tr>
                            }
                        </tbody>
                    </table>
                </div>
            </div>
            }

            <Modal
                show={showShiftForm}
                onHide={loading ? undefined : closeShiftForm}
                backdrop={loading ? "static" : true}
            >
                <Modal.Header closeButton={!loading}>
                    <Modal.Title>
                        {selectedShift ? "Editar turno" : "Nuevo turno"}
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <div className="form-group">
                        <label>Nombre del turno</label>
                        <input
                            type="text"
                            className="form-control"
                            value={shiftForm.name}
                            onChange={e =>
                                setShiftForm({
                                    ...shiftForm,
                                    name: e.target.value
                                })
                            }
                            disabled={loading}
                        />
                    </div>

                    <div className="row">
                        <div className="col-sm-6">
                            <div className="form-group">
                                <label>Hora inicial</label>
                                <input
                                    type="time"
                                    className="form-control"
                                    value={shiftForm.startTime}
                                    onChange={e =>
                                        setShiftForm({
                                            ...shiftForm,
                                            startTime: e.target.value
                                        })
                                    }
                                    disabled={loading}
                                />
                            </div>
                        </div>

                        <div className="col-sm-6">
                            <div className="form-group">
                                <label>Hora final</label>
                                <input
                                    type="time"
                                    className="form-control"
                                    value={shiftForm.endTime}
                                    onChange={e =>
                                        setShiftForm({
                                            ...shiftForm,
                                            endTime: e.target.value
                                        })
                                    }
                                    disabled={loading}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="form-group">
                        <label>Duración de comida</label>
                        <select
                            className="form-control"
                            value={shiftForm.mealDurationMinutes}
                            onChange={e =>
                                setShiftForm({
                                    ...shiftForm,
                                    mealDurationMinutes:
                                        Number(e.target.value)
                                })
                            }
                            disabled={loading}
                        >
                            <option value={30}>30 minutos</option>
                            <option value={45}>45 minutos</option>
                            <option value={60}>60 minutos</option>
                        </select>
                    </div>

                    <div className="checkbox">
                        <label>
                            <input
                                type="checkbox"
                                checked={shiftForm.isNight}
                                onChange={e =>
                                    setShiftForm({
                                        ...shiftForm,
                                        isNight: e.target.checked
                                    })
                                }
                                disabled={loading}
                            />
                            {" "}Turno nocturno
                        </label>
                    </div>

                    <div className="checkbox">
                        <label>
                            <input
                                type="checkbox"
                                checked={shiftForm.active}
                                onChange={e =>
                                    setShiftForm({
                                        ...shiftForm,
                                        active: e.target.checked
                                    })
                                }
                                disabled={loading}
                            />
                            {" "}Activo
                        </label>
                    </div>
                </Modal.Body>

                <Modal.Footer>
                    <button
                        type="button"
                        className="btn btn-default"
                        onClick={closeShiftForm}
                        disabled={loading}
                    >
                        Cancelar
                    </button>

                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={saveShift}
                        disabled={loading}
                    >
                        {loading ? "Guardando..." : "Guardar"}
                    </button>
                </Modal.Footer>
            </Modal>

            <Modal
                show={showResourceForm}
                onHide={loading ? undefined : closeResourceForm}
                backdrop={loading ? "static" : true}
            >
                <form onSubmit={saveResource}>
                    <Modal.Header closeButton={!loading}>
                        <Modal.Title>
                            {selectedResource
                                ? "Editar recurso"
                                : "Nuevo recurso"}
                        </Modal.Title>
                    </Modal.Header>

                    <Modal.Body>
                        <div style={{padding: "5px 5px 0"}}>

                            <div
                                style={{
                                    marginBottom: 22,
                                    paddingBottom: 8,
                                    borderBottom: "1px solid #e5e5e5"
                                }}
                            >
                                <div
                                    style={{
                                        fontSize: 12,
                                        fontWeight: 700,
                                        color: "#777",
                                        textTransform: "uppercase",
                                        letterSpacing: ".5px"
                                    }}
                                >
                                    Información del recurso
                                </div>
                            </div>

                            <div className="form-group">
                                <label>Nombre del recurso</label>

                                <input
                                    type="text"
                                    className="form-control"
                                    value={resourceForm.name}
                                    onChange={event =>
                                        setResourceForm({
                                            ...resourceForm,
                                            name: event.target.value
                                        })
                                    }
                                    disabled={loading}
                                    placeholder="Ej. Cabina SPA 1"
                                    required
                                />

                                <span className="help-block">
                                    Nombre con el que se identificará este recurso
                                    dentro de la operación del SPA.
                                </span>
                            </div>

                            <div className="row">
                                <div className="col-sm-6">
                                    <div className="form-group">
                                        <label>Tipo de recurso</label>

                                        <select
                                            className="form-control"
                                            value={resourceForm.resourceType}
                                            onChange={event =>
                                                setResourceForm({
                                                    ...resourceForm,
                                                    resourceType: event.target.value
                                                })
                                            }
                                            disabled={loading}
                                            required
                                        >
                                            <option value="">
                                                Selecciona un tipo...
                                            </option>

                                            {resourceTypes.map(type =>
                                                <option
                                                    key={type.id}
                                                    value={type.id}
                                                >
                                                    {type.name}
                                                </option>
                                            )}
                                        </select>
                                    </div>
                                </div>

                                <div className="col-sm-6">
                                    <div className="form-group">
                                        <label>Sala</label>

                                        <select
                                            className="form-control"
                                            value={resourceForm.lounge}
                                            onChange={event =>
                                                setResourceForm({
                                                    ...resourceForm,
                                                    lounge: event.target.value
                                                })
                                            }
                                            disabled={loading}
                                            required
                                        >
                                            <option value="">
                                                Selecciona una sala...
                                            </option>

                                            {lounges.map(lounge =>
                                                <option
                                                    key={lounge.id}
                                                    value={lounge.id}
                                                >
                                                    {lounge.name}
                                                </option>
                                            )}
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <div
                                style={{
                                    marginTop: 12,
                                    marginBottom: 20,
                                    paddingBottom: 8,
                                    borderBottom: "1px solid #e5e5e5"
                                }}
                            >
                                <div
                                    style={{
                                        fontSize: 12,
                                        fontWeight: 700,
                                        color: "#777",
                                        textTransform: "uppercase",
                                        letterSpacing: ".5px"
                                    }}
                                >
                                    Estado operativo
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-sm-7">
                                    <div className="form-group">
                                        <label>Disponibilidad</label>

                                        <select
                                            className="form-control"
                                            value={resourceForm.status}
                                            onChange={event =>
                                                setResourceForm({
                                                    ...resourceForm,
                                                    status: event.target.value
                                                })
                                            }
                                            disabled={loading}
                                        >
                                            <option value="AVAILABLE">
                                                Disponible
                                            </option>

                                            <option value="OUT_OF_SERVICE">
                                                Fuera de servicio
                                            </option>
                                        </select>

                                        <span className="help-block">
                                            Indica si el recurso se encuentra disponible
                                            actualmente para prestar servicio.
                                        </span>
                                    </div>
                                </div>

                                <div className="col-sm-5">
                                    <div className="form-group">
                                        <label>Estado del recurso</label>

                                        <div style={{marginTop: 6}}>
                                            <Switch
                                                value={resourceForm.active}
                                                onText="Sí"
                                                offText="No"
                                                onChange={(sw, value) =>
                                                    setResourceForm({
                                                        ...resourceForm,
                                                        active: value
                                                    })
                                                }
                                                disabled={loading}
                                            />

                                            <span
                                                style={{
                                                    marginLeft: 10,
                                                    fontWeight: 600
                                                }}
                                            >
                                                {resourceForm.active
                                                    ? "Activo"
                                                    : "Inactivo"}
                                            </span>
                                        </div>

                                        <span className="help-block">
                                            {resourceForm.active
                                                ? "El recurso participa en la operación del SPA."
                                                : "El recurso queda fuera de la operación sin eliminarse."}
                                        </span>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </Modal.Body>

                    <Modal.Footer>
                        <button
                            type="button"
                            className="btn btn-default"
                            onClick={closeResourceForm}
                            disabled={loading}
                        >
                            Cancelar
                        </button>

                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={loading}
                        >
                            {loading
                                ? "Guardando..."
                                : "Guardar recurso"}
                        </button>
                    </Modal.Footer>
                </form>
            </Modal>

            <Modal
                show={showForm}
                onHide={loading ? undefined : closeForm}
                backdrop={loading ? "static" : true}
            >
                <form onSubmit={save}>
                    <Modal.Header closeButton={!loading}>
                        <Modal.Title>
                            <i
                                className="fa fa-cutlery"
                                style={{marginRight: 9}}
                            />

                            {selectedService
                                ? "Editar servicio"
                                : "Nuevo servicio"}
                        </Modal.Title>
                    </Modal.Header>

                    <Modal.Body>
                        <div style={{padding: "5px 5px 0"}}>

                            <div
                                style={{
                                    marginBottom: 22,
                                    paddingBottom: 8,
                                    borderBottom: "1px solid #e5e5e5"
                                }}
                            >
                                <strong
                                    style={{
                                        fontSize: 12,
                                        color: "#777",
                                        textTransform: "uppercase"
                                    }}
                                >
                                    <i
                                        className="fa fa-list"
                                        style={{marginRight: 8}}
                                    />
                                    Información del servicio
                                </strong>
                            </div>

                            <div className="form-group">
                                <label>Nombre del servicio</label>

                                <input
                                    className="form-control"
                                    name="name"
                                    value={form.name}
                                    onChange={change}
                                    maxLength="255"
                                    required
                                    disabled={loading}
                                />

                                <p
                                    className="help-block"
                                    style={{marginBottom: 18}}
                                >
                                    Nombre con el que se identificará este servicio
                                    dentro de la operación del SPA.
                                </p>
                            </div>

                            <div className="row">
                                <div className="col-sm-6">
                                    <div className="form-group">
                                        <label>Duración (minutos)</label>

                                        <input
                                            type="number"
                                            className="form-control"
                                            name="durationMinutes"
                                            value={form.durationMinutes}
                                            onChange={change}
                                            min="1"
                                            step="1"
                                            required
                                            disabled={loading}
                                        />

                                        <p className="help-block">
                                            Tiempo estimado del servicio.
                                        </p>
                                    </div>
                                </div>

                                <div className="col-sm-6">
                                    <div className="form-group">
                                        <label>Tipo de recurso</label>

                                        <select
                                            className="form-control"
                                            name="resourceType"
                                            value={form.resourceType}
                                            onChange={change}
                                            disabled={loading}
                                        >
                                            <option value="">
                                                Sin tipo de recurso
                                            </option>

                                            {resourceTypes
                                                .filter(type =>
                                                    type.active !== false
                                                )
                                                .map(type => (
                                                    <option
                                                        key={type.id}
                                                        value={type.id}
                                                    >
                                                        {type.name}
                                                    </option>
                                                ))}
                                        </select>

                                        <p className="help-block">
                                            Recurso necesario para brindar este servicio.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="form-group">
                                <label>Observaciones</label>

                                <textarea
                                    className="form-control"
                                    name="observations"
                                    value={form.observations}
                                    onChange={change}
                                    rows="3"
                                    disabled={loading}
                                    placeholder="Información adicional del servicio"
                                />

                                <p className="help-block">
                                    Notas, indicaciones o consideraciones especiales.
                                </p>
                            </div>

                            <div
                                style={{
                                    marginTop: 22,
                                    marginBottom: 18,
                                    paddingBottom: 8,
                                    borderBottom: "1px solid #e5e5e5"
                                }}
                            >
                                <strong
                                    style={{
                                        fontSize: 12,
                                        color: "#777",
                                        textTransform: "uppercase"
                                    }}
                                >
                                    <i
                                        className="fa fa-cog"
                                        style={{marginRight: 8}}
                                    />
                                    Configuración operativa
                                </strong>
                            </div>

                            <div className="row">
                                <div className="col-sm-6">
                                    <label>Requiere personal</label>

                                    <div style={{marginTop: 6}}>
                                        <Switch
                                            value={form.requiresStaff}
                                            onText="Sí"
                                            offText="No"
                                            onChange={(sw, value) =>
                                                setForm({
                                                    ...form,
                                                    requiresStaff: value
                                                })
                                            }
                                            disabled={loading}
                                        />

                                        <span
                                            style={{
                                                marginLeft: 10,
                                                fontWeight: 600
                                            }}
                                        >
                                            {form.requiresStaff
                                                ? "Sí"
                                                : "No"}
                                        </span>
                                    </div>

                                    <p
                                        className="help-block"
                                        style={{marginTop: 8}}
                                    >
                                        Indica si este servicio requiere personal
                                        para su ejecución.
                                    </p>
                                </div>

                                <div className="col-sm-6">
                                    <label>Estado del servicio</label>

                                    <div style={{marginTop: 6}}>
                                        <Switch
                                            value={form.active}
                                            onText="Sí"
                                            offText="No"
                                            onChange={(sw, value) =>
                                                setForm({
                                                    ...form,
                                                    active: value
                                                })
                                            }
                                            disabled={loading}
                                        />

                                        <span
                                            style={{
                                                marginLeft: 10,
                                                fontWeight: 600
                                            }}
                                        >
                                            {form.active
                                                ? "Activo"
                                                : "Inactivo"}
                                        </span>
                                    </div>

                                    <p
                                        className="help-block"
                                        style={{marginTop: 8}}
                                    >
                                        El servicio participa en la operación del SPA.
                                    </p>
                                </div>
                            </div>

                        </div>
                    </Modal.Body>

                    <Modal.Footer>
                        <button
                            type="button"
                            className="btn btn-default"
                            onClick={closeForm}
                            disabled={loading}
                        >
                            Cancelar
                        </button>

                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={loading}
                        >
                            {loading
                                ? "Guardando..."
                                : "Guardar servicio"}
                        </button>
                    </Modal.Footer>
                </form>
            </Modal>
        </div>
    );
};

export default SpaConfiguration;
